import { api } from './api';
import type { Order } from '@/types/trading';

export interface PlaceOrderRequest {
  symbol: string;
  quantity?: number;
  notional?: number;
  side: 'buy' | 'sell';
  orderType: 'market' | 'limit';
  limitPrice?: number;
  timeInForce?: 'day' | 'gtc' | 'ioc' | 'opg';
  estimatedPrice?: number;
  riskOverride?: boolean;
}

export interface PlaceOrderResponse {
  order: Order;
  riskWarnings?: Array<{
    rule: string;
    message: string;
    severity: 'warning' | 'block';
  }>;
  requiresConfirmation: boolean;
  marketClosed?: boolean;
}

export interface OrderFilters {
  status?: string;
  symbol?: string;
  side?: string;
  limit?: number;
}

export const tradingService = {
  async placeOrder(request: PlaceOrderRequest): Promise<PlaceOrderResponse> {
    // Transform camelCase frontend fields to snake_case expected by backend
    const body: Record<string, unknown> = {
      symbol: request.symbol,
      side: request.side,
      order_type: request.orderType,
    };
    if (request.quantity !== undefined) {
      body.quantity = request.quantity;
    }
    if (request.notional !== undefined) {
      body.notional = request.notional;
    }
    if (request.limitPrice !== undefined) {
      body.limit_price = request.limitPrice;
    }
    if (request.timeInForce !== undefined) {
      body.time_in_force = request.timeInForce;
    }
    if (request.estimatedPrice !== undefined) {
      body.estimated_price = request.estimatedPrice;
    }
    const res = await api.post<Record<string, unknown>>('/orders', body);
    return {
      order: res.order as Order,
      riskWarnings: (res.risk_violations ?? res.riskWarnings) as PlaceOrderResponse['riskWarnings'],
      requiresConfirmation: Boolean(res.requires_confirmation ?? res.requiresConfirmation),
      marketClosed: Boolean(res.market_closed ?? res.marketClosed),
    };
  },

  async getOrders(filters?: OrderFilters): Promise<Order[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.symbol) params.append('symbol', filters.symbol);
    if (filters?.side) params.append('side', filters.side);
    if (filters?.limit) params.append('limit', String(filters.limit));

    const queryString = params.toString();
    const path = queryString ? `/orders?${queryString}` : '/orders';
    return api.get<Order[]>(path);
  },

  async getOrder(orderId: string): Promise<Order> {
    return api.get<Order>(`/orders/${orderId}`);
  },

  async cancelOrder(orderId: string): Promise<void> {
    await api.delete(`/orders/${orderId}`);
  },

  async overrideRiskWarning(orderId: string): Promise<Order> {
    return api.post<Order>(`/orders/${orderId}/override`);
  },

  async getMarketStatus(): Promise<{
    isOpen: boolean;
    isExtendedHours: boolean;
    timezone: string;
  }> {
    const res = await api.get<Record<string, unknown>>('/market/status');
    return {
      isOpen: Boolean(res.is_open ?? false),
      isExtendedHours: Boolean(res.is_extended_hours ?? false),
      timezone: String(res.timezone ?? 'America/New_York'),
    };
  },
};

export default tradingService;
